import { ICON_BUTTON_ICONS } from '../../components/buttons/IconButton';

/**
 * The Profiler result views, by name.
 *
 * TD-2720 — these used to be reached as `PROFILER_VIEWS_LIST[0|1|2].value`, which
 * made the array's order part of the app's logic: reordering the switch menu would
 * have silently re-pointed two dozen identity checks. The values live here so the
 * array below is free to be ordered for the menu, and the stored localStorage values
 * ('list' / 'heatmap' / 'chart') are unchanged, so saved per-search preferences keep
 * resolving.
 *
 * TD-2733 — this module is the single home for them. The saved-project and
 * team-feedback surfaces used to carry their own `PROFILER_PROJECT_VIEW_VIEWS_LIST`
 * with the same three entries, the same values and the same positional coupling. They
 * now read these, so the switch menu has one definition and one order everywhere. The
 * old array's entry `id`s (`PPV_VIEW_*`) were React keys only, so nothing depended on
 * them.
 */
export const PROFILER_VIEWS = {
  HEATMAP: 'heatmap',
  LIST: 'list',
  CHART: 'chart'
};

/**
 * TD-2720 — the heatmap is the standard view: it now carries the list underneath it,
 * so it is a superset of what List showed. Only searches with no stored preference
 * are affected; anyone who explicitly picked List on a given search keeps it.
 */
export const DEFAULT_PROFILER_VIEW = PROFILER_VIEWS.HEATMAP;

// Order here is the order of the switch menu — heatmap first, as the standard view.
export const PROFILER_VIEWS_LIST = [
  {
    id: 'PROFILER_VIEW_HEATMAP',
    name: 'Heatmap',
    value: PROFILER_VIEWS.HEATMAP,
    icon: ICON_BUTTON_ICONS.COLUMNS_GAP
  },
  {
    id: 'PROFILER_VIEW_LIST',
    name: 'List',
    value: PROFILER_VIEWS.LIST,
    icon: ICON_BUTTON_ICONS.VIEW_LIST
  },
  {
    id: 'PROFILER_VIEW_CHART',
    name: 'Chart',
    value: PROFILER_VIEWS.CHART,
    icon: ICON_BUTTON_ICONS.BAR_CHART_LINE
  }
];

/**
 * TD-2720 — does this Profiler view render the paginated list?
 *
 * The heatmap view stacks the heatmap *above* the list instead of replacing it, so
 * everything that used to be gated on "List view only" — the column-header row, the
 * pager, the demo-limit banner, the excluded-results panel, Customize columns, the
 * Add-filter entry point, the filter chip rail and bulk exclude — is gated on this
 * instead. Chart view still replaces the list, so it stays out.
 *
 * Takes the view value rather than the store so it stays a pure predicate (used from
 * the search page, the results body, deriveSearchResultsFlags, and — since TD-2733 —
 * the saved-project and team-feedback surfaces).
 */
export const isProfilerListVisible = (profilerView) =>
  profilerView === PROFILER_VIEWS.LIST || profilerView === PROFILER_VIEWS.HEATMAP;
